/**
 * SceneBigNumbers — kiosk scene displaying five critical reef parameters
 * (temp, pH, salinity, ORP, alkalinity) in enormous tabular numerals
 * legible from across the room. Falls back gracefully when a probe is absent.
 */
import { useApexSnapshot } from '../../../hooks/useApexSnapshot';
import { AnimatedNumber } from '../../../components/ui/AnimatedNumber';
import type { ApexProbeSnapshot } from '../../../lib/types';

/** Probe-name fragments used to identify each parameter. Case-insensitive. */
const PROBE_PATTERNS: Record<string, RegExp> = {
  temp: /temp|temperature/i,
  ph: /\bph\b/i,
  salinity: /salin|sal\b/i,
  orp: /\borp\b/i,
  alkalinity: /alk|dkh|kh\b/i,
};

interface BigParam {
  key: string;
  label: string;
  unit: string;
  decimals: number;
}

const PARAMS: BigParam[] = [
  { key: 'temp', label: 'Temperature', unit: '°F', decimals: 1 },
  { key: 'ph', label: 'pH', unit: '', decimals: 2 },
  { key: 'salinity', label: 'Salinity', unit: 'ppt', decimals: 1 },
  { key: 'orp', label: 'ORP', unit: 'mV', decimals: 0 },
  { key: 'alkalinity', label: 'Alkalinity', unit: 'dKH', decimals: 1 },
];

function findProbe(
  probes: ApexProbeSnapshot[],
  key: string,
): ApexProbeSnapshot | undefined {
  const pattern = PROBE_PATTERNS[key];
  if (!pattern) return undefined;
  return probes.find((p) => pattern.test(p.name));
}

export function SceneBigNumbers() {
  const { snapshot } = useApexSnapshot();
  const probes = snapshot?.apex?.probes ?? [];

  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center gap-8 px-8"
      data-testid="scene-big-numbers"
    >
      <h2 className="text-2xl font-semibold tracking-widest text-white/60 uppercase select-none">
        Live Parameters
      </h2>

      <div className="grid grid-cols-5 gap-6 w-full max-w-6xl">
        {PARAMS.map(({ key, label, unit, decimals }) => {
          const probe = findProbe(probes, key);
          const value = probe?.value;

          return (
            <div
              key={key}
              className="flex flex-col items-center gap-2"
              data-testid={`big-param-${key}`}
            >
              <span className="text-xs font-medium tracking-widest text-white/50 uppercase select-none">
                {label}
              </span>

              {value !== undefined ? (
                <span className="num text-7xl font-bold text-white leading-none">
                  <AnimatedNumber
                    value={value}
                    decimals={decimals}
                    className="num"
                    aria-label={`${label}: ${value.toFixed(decimals)} ${unit}`}
                    data-testid={`big-num-${key}`}
                  />
                </span>
              ) : (
                <span
                  className="text-7xl font-bold text-white/20 leading-none select-none"
                  aria-label={`${label}: unavailable`}
                  data-testid={`big-num-${key}`}
                >
                  —
                </span>
              )}

              {unit ? (
                <span className="text-sm text-white/40 select-none">{unit}</span>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
